import { BusinessConfig, WorkHours } from '../types';
import { dayjs, Dayjs } from './dayjs-config';
import {
  DAYS_OF_WEEK_PREFERRED_ORDER,
  ensureBusinessHoursShape,
  BUSINESS_HOURS_REGEX,
  BUSINESS_HOURS_NOTE_LIMIT,
} from '../constants';
import { convertTo24HourString, extractTimesFromBitString, getBitStringBetweenTimes } from '../time/timeUtils';

export type DayKey = keyof BusinessConfig['businessInfo']['businessHours'];

export type BusinessHoursEntry = {
  startTime: Dayjs | null;
  endTime: Dayjs | null;
  isClosed: boolean;
  walkInOnly: boolean;
};

export const WALK_IN_NOTE_LABEL = 'Walk-Ins Only';
const WALK_IN_NOTE_REGEX = /\(\s*walk-ins only\s*\)$/i;

export const createEmptyBusinessHoursEntry = (): BusinessHoursEntry => ({
  startTime: null,
  endTime: null,
  isClosed: false,
  walkInOnly: false,
});

const parseTimeToken = (token?: string): Dayjs | null => {
  if (!token) return null;
  const normalized = token.trim().toUpperCase();
  const parsedCompact = dayjs(normalized, 'h:mmA', true);
  if (parsedCompact.isValid()) return parsedCompact;
  const parsedSpaced = dayjs(normalized, 'h:mm A', true);
  return parsedSpaced.isValid() ? parsedSpaced : null;
};

export const parseBusinessHoursEntry = (raw?: string): BusinessHoursEntry => {
  const trimmed = (raw ?? '').trim();
  if (!trimmed) {
    return createEmptyBusinessHoursEntry();
  }

  if (trimmed.toUpperCase() === 'CLOSED') {
    return {
      startTime: null,
      endTime: null,
      isClosed: true,
      walkInOnly: false,
    };
  }

  const hasWalkInNote = WALK_IN_NOTE_REGEX.test(trimmed);
  const valueWithoutNote = hasWalkInNote ? trimmed.replace(WALK_IN_NOTE_REGEX, '').trim() : trimmed;
  const match = valueWithoutNote.match(BUSINESS_HOURS_REGEX);
  if (!match) {
    return {
      startTime: null,
      endTime: null,
      isClosed: false,
      walkInOnly: hasWalkInNote,
    };
  }

  const startTime = parseTimeToken(match[1]);
  const endTime = parseTimeToken(match[2]);

  return {
    startTime,
    endTime,
    isClosed: false,
    walkInOnly: hasWalkInNote,
  };
};

export const convertHoursToEntries = (
  hours?: Partial<BusinessConfig['businessInfo']['businessHours']> | null
) => {
  const shaped = ensureBusinessHoursShape(hours);
  return DAYS_OF_WEEK_PREFERRED_ORDER.reduce((acc, day) => {
    acc[day] = parseBusinessHoursEntry(shaped[day]);
    return acc;
  }, {} as Record<DayKey, BusinessHoursEntry>);
};

export const formatBusinessHoursEntry = (entry: BusinessHoursEntry): string => {
  if (!entry) return '';
  if (entry.isClosed) return 'CLOSED';
  if (!entry.startTime || !entry.endTime) return '';

  const start = entry.startTime.format('h:mmA').toUpperCase();
  const end = entry.endTime.format('h:mmA').toUpperCase();
  const base = `${start} - ${end}`;

  if (!entry.walkInOnly) {
    return base;
  }
  const note = WALK_IN_NOTE_LABEL.slice(0, BUSINESS_HOURS_NOTE_LIMIT);
  return `${base} (${note})`;
};

export const entriesToHoursMap = (
  entries: Record<DayKey, BusinessHoursEntry>
): BusinessConfig['businessInfo']['businessHours'] => {
  const shaped = ensureBusinessHoursShape();
  DAYS_OF_WEEK_PREFERRED_ORDER.forEach((day) => {
    shaped[day] = formatBusinessHoursEntry(entries[day]);
  });
  return shaped;
};

export const cloneBusinessHoursEntries = (
  entries: Record<DayKey, BusinessHoursEntry>
): Record<DayKey, BusinessHoursEntry> => {
  return DAYS_OF_WEEK_PREFERRED_ORDER.reduce((acc, day) => {
    const entry = entries[day] ?? createEmptyBusinessHoursEntry();
    acc[day] = {
      startTime: entry.startTime,
      endTime: entry.endTime,
      isClosed: entry.isClosed,
      walkInOnly: entry.walkInOnly,
    };
    return acc;
  }, {} as Record<DayKey, BusinessHoursEntry>);
};

export const convertWorkHoursToBusinessEntries = (
  workHours?: WorkHours | null
): Record<DayKey, BusinessHoursEntry> | null => {
  if (!workHours) {
    return null;
  }

  return DAYS_OF_WEEK_PREFERRED_ORDER.reduce((acc, day) => {
    const bitString = workHours[day as keyof WorkHours];
    if (!bitString) {
      acc[day] = createEmptyBusinessHoursEntry();
      acc[day].isClosed = true;
      return acc;
    }

    const schedule = extractTimesFromBitString(bitString);
    if (schedule.isDayOff) {
      acc[day] = {
        startTime: null,
        endTime: null,
        isClosed: true,
        walkInOnly: false,
      };
    } else {
      acc[day] = {
        startTime: schedule.startTime,
        endTime: schedule.endTime,
        isClosed: false,
        walkInOnly: false,
      };
    }

    return acc;
  }, {} as Record<DayKey, BusinessHoursEntry>);
};

export const convertBusinessHoursToWorkHours = (
  hours?: Partial<BusinessConfig['businessInfo']['businessHours']> | null
): WorkHours => {
  const entries = convertHoursToEntries(hours);
  const defaultWorkDayBitString = getBitStringBetweenTimes('0900', '1700', false);
  const closedDayBitString = getBitStringBetweenTimes('0000', '0000', false);
  const hasAnyConfiguredBusinessHours = DAYS_OF_WEEK_PREFERRED_ORDER.some((day) =>
    typeof hours?.[day] === 'string' && (hours[day] as string).trim().length > 0
  );

  const hasAnyNonZeroBusinessHours = DAYS_OF_WEEK_PREFERRED_ORDER.some((day) => {
    const entry = entries[day];
    if (entry.isClosed || !entry.startTime || !entry.endTime) {
      return false;
    }
    const startTime = convertTo24HourString(entry.startTime);
    const endTime = convertTo24HourString(entry.endTime);
    return !!startTime && !!endTime && startTime !== endTime;
  });

  return DAYS_OF_WEEK_PREFERRED_ORDER.reduce((acc, day) => {
    const entry = entries[day];

    if (entry.isClosed || !entry.startTime || !entry.endTime) {
      acc[day] = hasAnyNonZeroBusinessHours || hasAnyConfiguredBusinessHours
        ? closedDayBitString
        : defaultWorkDayBitString;
      return acc;
    }

    const startTime = convertTo24HourString(entry.startTime);
    const endTime = convertTo24HourString(entry.endTime);

    if (!startTime || !endTime || startTime === endTime) {
      acc[day] = hasAnyNonZeroBusinessHours || hasAnyConfiguredBusinessHours
        ? closedDayBitString
        : defaultWorkDayBitString;
      return acc;
    }

    acc[day] = getBitStringBetweenTimes(startTime, endTime, false);
    return acc;
  }, {} as WorkHours);
};
